/**
 * PreviewHeader — Header with nav, dark toggle, mobile menu
 */
import { useState, useEffect } from 'react';
import { resolveNavHref } from '@/generators/html/content-pages/nav-helper';

interface PreviewHeaderProps {
  data: {
    logoUrl?: string;
    logoText?: string;
    companyName?: string;
    navItems?: string[];
    ctaText?: string;
  };
  settings: {
    darkModeToggle?: boolean;
    darkMode?: boolean;
    [key: string]: any;
  };
  onToggleDarkMode?: () => void;
}

export function PreviewHeader({ data, settings, onToggleDarkMode }: PreviewHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [mobileMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  return (
    <header className="preview-header">
      <div className="preview-container">
        <div className="preview-header__inner">
          {/* Brand */}
          <a href="/" className="preview-header__brand">
            {data.logoUrl ? (
              <img
                src={data.logoUrl}
                alt={data.companyName}
                className="preview-header__logo"
                style={{ width: 44, height: 44, objectFit: "contain" }}
              />
            ) : (
              <div className="preview-header__logo">{data.logoText}</div>
            )}
            <span className="preview-header__name">
              {data.companyName}
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="preview-header__nav">
            {data.navItems?.map((item, i) => (
              <a
                key={i}
                href={resolveNavHref(item, settings, false)}
                className="preview-header__nav-link"
              >
                {item}
              </a>
            ))}

            {settings.darkModeToggle && (
              <button
                className="preview-header__dark-toggle"
                aria-label={
                  settings.darkMode
                    ? "Light Mode aktivieren"
                    : "Dark Mode aktivieren"
                }
                aria-pressed={settings.darkMode}
                role="switch"
                data-active={settings.darkMode ? "true" : "false"}
                onClick={onToggleDarkMode}
              />
            )}

            <button className="preview-header__cta">
              {data.ctaText}
            </button>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            className={`preview-header__toggle ${mobileMenuOpen ? "open" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Menu schliessen" : "Menu oeffnen"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Mobile Navigation with Backdrop */}
      <nav
        id="mobile-nav"
        className={`preview-header__mobile-nav ${mobileMenuOpen ? "open" : ""}`}
        onClick={(e) =>
          e.target === e.currentTarget && setMobileMenuOpen(false)
        }
        aria-hidden={!mobileMenuOpen}
      >
        <div className="preview-header__mobile-nav-panel">
          {data.navItems?.map((item, i) => (
            <a
              key={i}
              href={resolveNavHref(item, settings, false)}
              onClick={() => setMobileMenuOpen(false)}
            >
              {item}
            </a>
          ))}
          <button
            className="preview-btn preview-btn--primary"
            style={{ marginTop: "auto" }}
          >
            {data.ctaText}
          </button>
        </div>
      </nav>
    </header>
  );
}
